import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getControlledEvidence, getControlledVersions, openControlledAuthoringDraft } from '../../api/authoringApi';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileCheck2,
  Fingerprint,
  History,
  LockKeyhole,
  MessageSquarePlus,
  Send,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { decideUpdateRequest, getControlledDocumentPreview } from '../../api/eqmsApi';
import { EQMS_QUERY_KEYS, useControlledDocument, useUpdateRequests } from '../../hooks/useEqmsDocuments';
import type { EqmsAction, EqmsStatus } from '../../types/eqms.types';
import { DocumentLifecycleRail } from './DocumentLifecycleRail';
import { DocumentUpdateRequests } from './update-requests/DocumentUpdateRequests';
import { RequestUpdateDialog } from './update-requests/RequestUpdateDialog';
import { UPDATE_REQUEST_STATUS_STYLES } from './update-requests/statusStyles';

const STATUS_LABELS: Record<EqmsStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  effective: 'Effective',
  obsolete: 'Obsolete',
  discarded: 'Discarded',
};

interface ControlledDocumentDetailDialogProps {
  documentId: string | null;
  currentUserId?: string;
  /** Tab to open first, e.g. `update-requests` from an owner notification link. */
  initialTab?: string;
  onClose: () => void;
  onAction: (action: EqmsAction, documentId: string, documentName: string, snapshotId?: string) => void;
}

export function ControlledDocumentDetailDialog({
  documentId,
  currentUserId,
  initialTab,
  onClose,
  onAction,
}: ControlledDocumentDetailDialogProps) {
  const detailQuery = useControlledDocument(documentId);
  const { t, i18n } = useTranslation('qms');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [requestUpdateOpen, setRequestUpdateOpen] = useState(false);
  const [revisionRequestIds, setRevisionRequestIds] = useState<string[]>([]);
  const versionsQuery = useQuery({ queryKey: ['eqms', 'versions', documentId], queryFn: () => getControlledVersions(documentId!), enabled: Boolean(documentId) });
  const authoring = useMutation({ mutationFn: (reason?: string) => openControlledAuthoringDraft(documentId!, reason), onSuccess: ({ draft }) => { setRevisionOpen(false); onClose(); navigate(`/eqms/authoring/${draft.id}`); } });
  const evidence = useMutation({ mutationFn: () => getControlledEvidence(documentId!), onSuccess: (manifest) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }));
    const anchor = window.document.createElement('a'); anchor.href = url; anchor.download = `${detailQuery.data?.document.controlCode || 'document'}-evidence.json`; anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }, onError: (error) => toast.error(error.message) });
  const detail = detailQuery.data;
  const document = detail?.document;
  const ownerIsCurrentUser = Boolean(document && currentUserId && document.ownerUserId === currentUserId);
  const ownerApprovalBlocked = ownerIsCurrentUser && detail?.permissions?.canApproveOwnDocument !== true;
  // Owners see every request for their document; anyone else only their own.
  const updateRequestsQuery = useUpdateRequests(
    { scope: ownerIsCurrentUser ? 'owner' : 'mine', documentId: documentId ?? undefined },
    Boolean(documentId && document),
  );
  const documentRequests = updateRequestsQuery.data?.requests;
  const pendingRequests = (documentRequests ?? []).filter((request) => ['open', 'addressing', 'parked'].includes(request.status));
  const updateRequestCount = ownerIsCurrentUser ? pendingRequests.length : documentRequests?.length ?? 0;

  const openRevisionDialog = () => {
    setRevisionReason('');
    // Parked requests were waiting for exactly this update.
    setRevisionRequestIds(pendingRequests.filter((request) => request.status !== 'open').map((request) => request.id));
    setRevisionOpen(true);
  };
  const startRevision = async () => {
    const toAddress = pendingRequests.filter((request) => revisionRequestIds.includes(request.id) && request.status !== 'addressing');
    const results = await Promise.allSettled(toAddress.map((request) =>
      decideUpdateRequest(request.id, { status: 'addressing', expectedStatus: request.status })));
    if (results.some((result) => result.status === 'rejected')) toast.warning(t('updateRequests.partialFailure'));
    if (toAddress.length) void queryClient.invalidateQueries({ queryKey: EQMS_QUERY_KEYS.updateRequests });
    authoring.mutate(revisionReason.trim());
  };

  const preview = async (versionId?: string) => {
    if (!documentId) return;
    try {
      const result = await getControlledDocumentPreview(documentId, versionId || document?.currentVersion?.id);
      window.open(result.previewUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open document preview');
    }
  };

  const act = (action: EqmsAction) => {
    if (document) onAction(action, document.id, document.name, document.currentVersion?.authoringSnapshotId || undefined);
  };
  // The withdrawal signature is the record of who discarded the draft and why.
  const discardSignature = detail?.signatures.find((signature) => signature.toState === 'discarded');
  const actionButtons = document ? {
    draft: [
      // An open revision is shown as a draft, but discarding applies only to a
      // document that never took effect.
      ...(document.revisionStatus ? [] : [
        <Button key="discard" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => act('discard')}>
          <Trash2 className="mr-2 h-4 w-4" />{t('discard.action', { defaultValue: 'Discard draft' })}
        </Button>,
      ]),
      document.sourceKind === 'authored'
        ? <Button key="edit" disabled={!detail?.authoring?.canEdit || authoring.isPending} onClick={() => authoring.mutate(undefined)}><Send className="mr-2 h-4 w-4" />{t('authoring.editDraft')}</Button>
        : <Button key="submit" onClick={() => act('submit_review')}><Send className="mr-2 h-4 w-4" /> Submit for review</Button>,
    ],
    in_review: [
      <Button key="reject" variant="outline" onClick={() => act('reject')}>
        <XCircle className="mr-2 h-4 w-4" /> Reject
      </Button>,
      <Button
        key="approve"
        onClick={() => act('approve')}
        disabled={ownerApprovalBlocked}
        title={ownerApprovalBlocked ? 'The owner cannot approve their own document' : undefined}
      >
        <CheckCircle2 className="mr-2 h-4 w-4" /> Approve
      </Button>,
    ],
    approved: [
      <Button
        key="release"
        onClick={() => act('make_effective')}
        className="bg-teal-700 hover:bg-teal-800"
      >
        <LockKeyhole className="mr-2 h-4 w-4" /> Make effective
      </Button>,
    ],
    effective: [
      ...(detail?.authoring?.canCreateRevision ? [<Button key="revision" variant="outline" onClick={openRevisionDialog}>{t('authoring.createRevision')}</Button>] : []),
      <Button key="obsolete" variant="destructive" onClick={() => act('obsolete')}>
        <AlertTriangle className="mr-2 h-4 w-4" /> Make obsolete
      </Button>,
    ],
    obsolete: [],
    discarded: [],
  }[document.lifecycleStatus] : [];

  return (
    <><Dialog open={Boolean(documentId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0">
        {detailQuery.isError ? <div role="alert" className="p-6"><p>{detailQuery.error.message}</p><Button className="mt-3" onClick={() => { void detailQuery.refetch(); }}>{t('authoring.retry')}</Button></div> : detailQuery.isLoading || !document ? (
          <div className="space-y-5 p-6">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-80 w-full" />
          </div>
        ) : (
          <>
            <DialogHeader className="border-b bg-slate-950 px-6 py-5 text-white">
              <div className="flex flex-col gap-4 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Badge className="border-white/15 bg-white/10 font-mono text-white hover:bg-white/10">
                      {document.controlCode}
                    </Badge>
                    <span className="font-mono text-xs text-slate-400">v{document.controlledVersion}</span>
                  </div>
                  <DialogTitle className="text-xl text-white">{document.name}</DialogTitle>
                  <DialogDescription className="mt-1 text-slate-300">
                    {document.department} · Owner: {document.ownerName}
                  </DialogDescription>
                </div>
                <div className="flex flex-wrap gap-2">{document.lifecycleStatus === 'effective' && <Button variant="secondary" size="sm" onClick={() => setRequestUpdateOpen(true)}><MessageSquarePlus className="mr-2 h-4 w-4" />{t('updateRequests.request')}</Button>}<Button variant="secondary" size="sm" disabled={evidence.isPending} onClick={() => evidence.mutate()}>{t('authoring.downloadEvidence')}</Button><Button variant="secondary" size="sm" onClick={() => { void preview(); }}>
                  <ExternalLink className="mr-2 h-4 w-4" /> Preview source
                </Button></div>
              </div>
            </DialogHeader>

            <div className="border-b bg-white px-6 py-5">
              {document.effectiveControlledVersion && document.revisionStatus && <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900"><span>{t('authoring.effectiveRelease', { version: document.effectiveControlledVersion })}</span><Button size="sm" variant="outline" onClick={() => { void preview(document.effectiveVersionId || undefined); }}>{t('authoring.viewVersion')}</Button></div>}
              {authoring.isError && <p role="alert" className="mb-3 text-sm text-red-700">{authoring.error.message}</p>}
              {document.lifecycleStatus === 'discarded' ? (
                <div role="status" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                  <p className="flex items-center gap-2 font-semibold"><XCircle className="h-4 w-4" />{t('discard.bannerTitle', { defaultValue: 'Discarded draft' })}</p>
                  <p className="mt-1">{t('discard.bannerBody', { defaultValue: 'This draft never took effect and is no longer in the register. Its file, signatures and audit trail are kept as evidence, and its control code can be reused.' })}</p>
                  {discardSignature && (
                    <p className="mt-2 text-red-800">
                      {t('discard.bannerBy', { defaultValue: 'Discarded by {{name}} on {{date}}:', name: discardSignature.signerName, date: new Date(discardSignature.signedAt).toLocaleString(i18n.language, { timeZone: 'America/Montreal', dateStyle: 'medium', timeStyle: 'short' }) })}{' '}
                      <span className="whitespace-pre-wrap break-words italic">{discardSignature.reason}</span>
                    </p>
                  )}
                </div>
              ) : <DocumentLifecycleRail status={document.revisionStatus || document.lifecycleStatus} />}
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  Current state: <span className="font-semibold text-slate-900">{STATUS_LABELS[document.lifecycleStatus]}</span>
                  {ownerApprovalBlocked && document.lifecycleStatus === 'in_review' && (
                    <span className="ml-2 text-amber-700">Owner approval is blocked by segregation of duties.</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">{actionButtons}</div>
              </div>
            </div>

            <Tabs defaultValue={initialTab === 'update-requests' ? 'update-requests' : 'signatures'} className="min-h-0 flex-1">
              <div className="border-b px-6">
                <TabsList className="h-12 bg-transparent p-0">
                  <TabsTrigger value="signatures" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600">
                    <Fingerprint className="mr-2 h-4 w-4" /> Signatures ({detail.signatures.length})
                  </TabsTrigger>
                  <TabsTrigger value="audit" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600">
                    <History className="mr-2 h-4 w-4" /> Audit trail ({detail.auditEvents.length})
                  </TabsTrigger>
                  <TabsTrigger value="versions" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600">{t('authoring.versions')}</TabsTrigger>
                  <TabsTrigger value="record" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600">
                    <FileCheck2 className="mr-2 h-4 w-4" /> Record
                  </TabsTrigger>
                  <TabsTrigger value="update-requests" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600">
                    <MessageSquarePlus className="mr-2 h-4 w-4" /> {t('updateRequests.tab')} ({updateRequestCount})
                  </TabsTrigger>
                </TabsList>
              </div>

              <ScrollArea className="h-[360px]">
                <TabsContent value="signatures" className="m-0 space-y-3 p-6">
                  {detail.signatures.length === 0 && (
                    <p className="py-10 text-center text-sm text-muted-foreground">No signatures recorded.</p>
                  )}
                  {detail.signatures.map((signature) => (
                    <div key={signature.id} className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-lg bg-teal-50 p-2 text-teal-700">
                          <ShieldCheck className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-medium text-slate-900">{signature.signerName}</div>
                            <div className="font-mono text-xs text-slate-500">
                              {new Date(signature.signedAt).toISOString()}
                            </div>
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            Meaning: <span className="font-medium capitalize text-slate-900">{signature.meaning}</span>
                            {' · '}
                            {signature.fromState ? STATUS_LABELS[signature.fromState] : 'Uncontrolled'}
                            {' → '}
                            {STATUS_LABELS[signature.toState]}
                          </div>
                          <p className="mt-2 text-sm text-slate-700">{signature.reason}</p>
                          {signature.snapshotId && <p className="mt-2 break-all font-mono text-[10px] text-slate-400">{t('authoring.signedSnapshot')}: {signature.snapshotId}<br />SHA-256: {signature.pdfChecksum}</p>}
                          <div className="mt-2 font-mono text-[11px] text-slate-400">{signature.username}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="audit" className="m-0 p-6">
                  <div className="relative space-y-0 border-l border-slate-200 pl-5">
                    {detail.auditEvents.map((event) => (
                      <div key={event.logId} className="relative pb-5">
                        <div className="absolute -left-[25px] top-1.5 h-2 w-2 rounded-full bg-teal-600 ring-4 ring-white" />
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="font-mono text-[10px]">{event.actionType}</Badge>
                          <span className="text-sm font-medium text-slate-900">{event.userName || 'Database control'}</span>
                          <span className="font-mono text-[11px] text-slate-400">
                            {new Date(event.timestamp).toISOString()}
                          </span>
                        </div>
                        {event.reason && <p className="mt-1 text-sm text-slate-600">{event.reason}</p>}
                        {event.metadata && (
                          <div className="mt-1 font-mono text-[11px] text-slate-400">
                            {JSON.stringify(event.metadata)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="versions" className="m-0 space-y-3 p-6">
                  {versionsQuery.isLoading ? <Skeleton className="h-20 w-full" /> : versionsQuery.isError ? <div role="alert"><p className="text-sm text-red-700">{versionsQuery.error.message}</p><Button variant="outline" className="mt-2" onClick={() => { void versionsQuery.refetch(); }}>{t('authoring.retry')}</Button></div> : versionsQuery.data?.versions.length ? versionsQuery.data.versions.map((version) => <div key={version.id} className="rounded-xl border border-slate-200 p-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="break-words text-sm font-medium">{version.originalName}</p><p className="mt-1 text-xs text-slate-500">{version.controlledVersion || version.version || ''}{version.createdAt ? ` · ${new Date(version.createdAt).toISOString()}` : ''}</p></div><Button variant="outline" size="sm" onClick={() => { void preview(version.id); }}>{t('authoring.viewVersion')}</Button></div>{(version.pdfChecksum || version.snapshot?.pdfChecksum) && <p className="mt-3 break-all font-mono text-[10px] text-slate-400">SHA-256: {version.pdfChecksum || version.snapshot?.pdfChecksum}</p>}</div>) : <p className="text-sm text-slate-500">{t('authoring.noVersions')}</p>}
                </TabsContent>
                <TabsContent value="update-requests" className="m-0 p-6">
                  <DocumentUpdateRequests isOwner={ownerIsCurrentUser} requests={documentRequests}
                    isLoading={updateRequestsQuery.isLoading} error={updateRequestsQuery.error} />
                </TabsContent>
                <TabsContent value="record" className="m-0 p-6">
                  <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                    {[
                      ['Control ID', document.controlCode],
                      ['Controlled version', `v${document.controlledVersion}`],
                      ['Lifecycle state', STATUS_LABELS[document.lifecycleStatus]],
                      ['Training', document.trainingRequired === false ? 'Not required' : 'Required'],
                      ['Department', document.department],
                      ['Owner', document.ownerName],
                      ['Source file', document.currentVersion?.originalName || '—'],
                      ['Approved (UTC)', document.approvedAt ? new Date(document.approvedAt).toISOString() : '—'],
                      ['Effective (UTC)', document.effectiveAt ? new Date(document.effectiveAt).toISOString() : '—'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
                        <dd className="mt-1 text-sm text-slate-900">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={revisionOpen} onOpenChange={setRevisionOpen}><DialogContent className="max-h-[90dvh] overflow-y-auto"><DialogHeader><DialogTitle>{t('authoring.revisionTitle')}</DialogTitle><DialogDescription>{t('authoring.revisionHint')}</DialogDescription></DialogHeader>
      {ownerIsCurrentUser && pendingRequests.length > 0 && <fieldset className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
        <legend className="px-1 text-sm font-medium text-amber-900">{t('updateRequests.revisionPending')}</legend>
        {pendingRequests.map((request) => <label key={request.id} className="flex items-start gap-3 rounded-md bg-white p-2 text-sm">
          <Checkbox className="mt-0.5" aria-label={`${t('updateRequests.revisionInclude')}: ${request.reason.slice(0, 60)}`}
            checked={request.status === 'addressing' || revisionRequestIds.includes(request.id)} disabled={request.status === 'addressing'}
            onCheckedChange={(checked) => setRevisionRequestIds((ids) => checked === true ? [...ids, request.id] : ids.filter((id) => id !== request.id))} />
          <span className="min-w-0"><span className={`mr-2 inline-block rounded border px-1.5 text-[11px] ${UPDATE_REQUEST_STATUS_STYLES[request.status]}`}>{t(`updateRequests.status.${request.status}`)}</span><span className="font-medium">{request.requesterName}</span><span className="block whitespace-pre-wrap break-words text-slate-700">{request.reason}</span></span>
        </label>)}
        <p className="text-xs text-amber-900">{t('updateRequests.autoClose')}</p>
      </fieldset>}
      <Label htmlFor="revision-reason">{t('authoring.revisionReason')}</Label><Textarea id="revision-reason" value={revisionReason} onChange={(event) => setRevisionReason(event.target.value)} />{authoring.isError && <p role="alert" className="text-sm text-red-700">{authoring.error.message}</p>}<DialogFooter><Button variant="outline" onClick={() => setRevisionOpen(false)}>{t('authoring.cancel')}</Button><Button disabled={revisionReason.trim().length < 3 || authoring.isPending} onClick={() => { void startRevision(); }}>{t('authoring.createRevision')}</Button></DialogFooter></DialogContent></Dialog>
    {document && <RequestUpdateDialog open={requestUpdateOpen} onOpenChange={setRequestUpdateOpen} documentId={document.id}
      documentLabel={`${document.controlCode} · ${document.name}`} ownerName={document.ownerName} />}</>
  );
}
